import React from 'react'
import { BreadcrumbJsonLd, NextSeo } from 'next-seo';
import SimilarCats from '../components/SimilarCats';
import { openStageBuilderPopup } from '../components/StageBuilderComp';
import ProductList from '../components/ProductList';
import { useDispatch, useSelector } from 'react-redux';
import { wrapper } from '../store/configStore';
import { SERVER_IP } from '../store/middleware/api';
import axios from 'axios';
import ClientReviews from '../components/ClientReviews';
import NotSure from '../components/NotSure';
import LandingCategories from '../components/LandingCategories';
import PastEvents from '../components/PastEvents';
import { getFilteredProducts, productsReceived, productsSetFilters } from '../store/slices/entities/products';
import ledVideoWallHeroV2 from '../public/Pictures/categories-v2/led-video-wall-rental-hero-v2.jpg'
import ob1 from '../public/Pictures/ob-videowall.jpg'
import vw_b from '../public/Pictures/video-wall-b.jpg'
import { FaArrowsAltH } from "@react-icons/all-files/fa/FaArrowsAltH";
import { MdFullscreen } from "@react-icons/all-files/md/MdFullscreen";
import { AiOutlineFullscreen } from "@react-icons/all-files/ai/AiOutlineFullscreen";
import OverBanner from '../components/OverBanner';
import PageShell from '../components/design-system/foundations/PageShell';
import ServiceHero from '../components/design-system/marketing/ServiceHero';
import ServiceOverview from '../components/design-system/marketing/ServiceOverview';
import VisualDecisionGuide from '../components/design-system/marketing/VisualDecisionGuide';
import VideoWallRentalOptions from '../components/design-system/marketing/VideoWallRentalOptions';
import RentalFAQV2 from '../components/design-system/marketing/RentalFAQV2';
import QuotePop from '../components/DeferredQuotePopup';
import { SHOW_POPUP } from '../store/slices/ui';
import { FiMessageCircle } from '@react-icons/all-files/fi/FiMessageCircle';
import { FiLayers } from '@react-icons/all-files/fi/FiLayers';
import { FiMapPin } from '@react-icons/all-files/fi/FiMapPin';
import { FiMonitor } from '@react-icons/all-files/fi/FiMonitor';
import { FiSliders } from '@react-icons/all-files/fi/FiSliders';
import * as gtag from '../lib/gtag';
import { compactCatalogProducts } from '../utilities/catalogPublicPayload';
import { compactPublicEvents } from '../utilities/eventPublicPayload';
import { reconcileLedWallFaq } from '../utilities/ledWallFaq';
import {
    getVisualFamilyBreadcrumbs,
    toBreadcrumbJsonLdItems,
    toHeroBreadcrumbs
} from '../utilities/visualFamilyBreadcrumbs';

const videoWallOverviewContent = {
    id: 'video-wall-rental-overview',
    heading: 'Need an LED video wall for your next event?',
    body: [
        'Choose an indoor or outdoor LED video wall sized for the venue, content, and closest viewing distance. Our team can help determine the right dimensions, pixel pitch, and mounting method.',
        'LED walls support presentation graphics, sponsor content, playback, and live camera feeds at conferences, trade shows, concerts, festivals, worship services, and private events.'
    ],
    guide: {
        heading: 'LED Video Wall Advantages',
        body: (
            <ol>
                <li>Superior brightness and color accuracy, even in well-lit environments.</li>
                <li>Flexible sizing and shape customization for creative display configurations.</li>
                <li>Outdoor-rated configurations for appropriate weather and venue conditions.</li>
                <li>Wide viewing angles for clear visibility from different positions.</li>
                <li>Visually stunning impact with high-resolution dynamic content.</li>
                <li>Ground-supported and truss-mounted options for different event layouts.</li>
            </ol>
        )
    },
    media: { src: vw_b, alt: 'Large LED video wall displaying event content in a professional venue' }
}


export const getStaticProps = wrapper.getStaticProps(
    async ({ store, preview }) => {
        const corp = await axios.request(`${SERVER_IP}/user/static-landing`)
        const subCate = await axios.request(`${SERVER_IP}/subcategory/name/Video-wall?cat=visuals`)
        const products = await axios.request(`${SERVER_IP}/inventory/product?msr=true&cat=video-wall`)
        const products2 = await axios.request(`${SERVER_IP}/inventory/product?msr=true&cat=led-panels`)
         const faq = await axios.request(`${SERVER_IP}/faq?category=Videowall`)
        store.dispatch({ type: productsReceived.type, payload: compactCatalogProducts([...products.data, ...products2.data]) })
        store.dispatch({ type: productsSetFilters.type, payload: { static: true } })
        let { newsletterevents, total_events } = corp?.data
        return {
            props: {
                newsletterevents: compactPublicEvents(newsletterevents), total_events, subCate: subCate.data, faq: reconcileLedWallFaq(faq.data)
            }
        }
    }
);

const VideoWallRental = ({ newsletterevents, total_events, subCate, faq}) => {
    let products = useSelector(getFilteredProducts)
    const dispatch = useDispatch()
    const openQuote = () => {
        gtag.event({ action: 'quote_click', label: 'leads', value: 3, category: 'orders' })
        dispatch(SHOW_POPUP({
            key: 'quote',
            size: 'medium',
            body: <QuotePop />
        }))
    }
    const openVideoWallBuilder = () => {
        gtag.event({ action: 'vw_builder_click', label: 'leads', value: 3, category: 'orders' })
        openStageBuilderPopup({ dispatch, videowall: true })
    }
    const heroSteps = [
        { icon: 'search', title: 'Choose your wall', description: 'Select the size, resolution, and mounting style.' },
        { icon: 'calendar', title: 'Share dates & venue', description: 'Tell us where and when the event takes place.' },
        { icon: 'truck', title: 'Delivery & installation', description: 'Our crew builds, connects, and tests the display.' },
        { icon: 'success', title: 'Show-ready visuals', description: 'Present content with local technical support.' }
    ]
    const breadcrumbs = getVisualFamilyBreadcrumbs('/led-video-wall-rental')
    const costFactors = [
        {
            label: 'Display configuration',
            title: 'Wall size and pixel pitch',
            icon: FiLayers,
            summary: 'Larger walls require more LED panels. A smaller pixel pitch provides finer detail for close viewing, while wider pitches can be practical at longer distances.',
            points: ['Overall wall dimensions', 'Closest viewer distance', 'Fine text or detailed content']
        },
        {
            label: 'Installation plan',
            title: 'Mounting and venue access',
            icon: FiMapPin,
            summary: 'Ground-supported and truss-mounted systems have different structural, labor, and access requirements that affect the final proposal.',
            points: ['Indoor or outdoor environment', 'Load-in path and setup window', 'Rigging or ground-support requirements']
        },
        {
            label: 'Show requirements',
            title: 'Processing and technical support',
            icon: FiSliders,
            summary: 'The source plan determines whether the system needs simple playback, presentation switching, live cameras, or an on-site technician during the event.',
            points: ['Laptop and playback sources', 'Switching or live camera feeds', 'Event-time technical operation']
        }
    ]
    return <>
        <BreadcrumbJsonLd itemListElements={toBreadcrumbJsonLdItems(breadcrumbs)} />
        <NextSeo
            title='LED Video Wall Rental in Miami & South Florida'
            description='Rent indoor and outdoor LED video walls with professional delivery, installation, processing, and technical support in Miami, Fort Lauderdale, and South Florida.'
            canonical="https://miamisoundrental.com/led-video-wall-rental"
            twitter={{
                handle: '@miamisoundrent1',
                site: '@miamisoundrent1',
                cardType: 'summary_large_image',
            }}
            openGraph={{
                type: 'website',
                url: `https://miamisoundrental.com/led-video-wall-rental`,
                title: 'LED Video Wall Rental in Miami & South Florida',
                description: 'Rent indoor and outdoor LED video walls with professional delivery, installation, processing, and technical support in Miami, Fort Lauderdale, and South Florida.',
                images: [
                    {
                        url: 'https://miamisoundrental.com/Pictures/categories-v2/led-video-wall-rental-hero-v2.jpg',
                        width: 1915,
                        height: 821,
                        alt: 'Large LED video wall installed for a corporate conference by Miami Sound Rental',
                    },
                ],
                site_name: 'Miami Sound Rental',
            }}
        />
        <div className='categoryRental projectorRental'>
            <PageShell>
                <ServiceHero
                    breadcrumbs={toHeroBreadcrumbs(breadcrumbs)}
                    eyebrow='LED video wall rental · Miami & Fort Lauderdale'
                    title='Video Wall & LED Screen Rental in Miami'
                    description='Create a high-brightness, large-format display for conferences, trade shows, concerts, and outdoor events, with professional delivery and installation.'
                    image={{ src: ledVideoWallHeroV2, position: 'center' }}
                    imageAlt='Large LED video wall installed for a corporate conference by Miami Sound Rental'
                    primaryAction={{
                        label: 'Configure Video Wall',
                        icon: FiMonitor,
                        conversionKey: 'led-video-wall-rental.hero.configure-wall',
                        channel: 'builder',
                        salesPath: 'self_service',
                        onClick: openVideoWallBuilder
                    }}
                    secondaryAction={{
                        label: 'Request Video Wall Proposal',
                        icon: FiMessageCircle,
                        conversionKey: 'led-video-wall-rental.hero.talk-to-expert',
                        channel: 'quote',
                        salesPath: 'managed',
                        onClick: openQuote
                    }}
                    stackActionsOnMobile
                    processLabel='How LED video wall rental works'
                    steps={heroSteps}
                />
            </PageShell>
            <PageShell>
                <VideoWallRentalOptions onConfigure={openVideoWallBuilder} />
            </PageShell>
            <NotSure
                top
                eyebrow='Managed video wall planning'
                title='Need a custom or show-operated LED wall?'
                description='Request a proposal for custom structures, multiple displays, content playback, switching, or technical operation.'
                quoteLabel='Request Video Wall Proposal'
            />
            <section className='stageType '>        
     
          
                <ProductList
                   carousel={{
                    name: 'LED Video Wall Rentals',
                    url: 'https://miamisoundrental.com/led-video-wall-rental',
                    description: 'LED Video Wall bundles'
                }}
                filter={[['Truss Mounted', 'hanged'], ['Frameless','Frameless'], ['DJ Booth', 'Booth']]}  sortBy={{      
         Pitch: ['darkSlateBlue', <FaArrowsAltH key='bolt' style={{color: 'darkSlateBlue'}} />],
    Resolution: ['green', <MdFullscreen key='bolt' style={{color: 'green'}} />],
    'Screen Area': ['darkGoldenRod', <AiOutlineFullscreen key='group' style={{color: 'darkGoldenRod'}}  />],
    }}  listName="Video Wall Bundles" anchor products={products.filter(p => p.categories?.includes('Visuals/Video Wall') && !p.tags.includes('noshow')).sort((a,b)=> b.tags?.includes('Top Seller') - a.tags?.includes('Top Seller')) } />
        
            </section>     
            <OverBanner picture={ob1} className='catEx' >
                <h2 className='bold'>LED Screen Rental Guide</h2>
                <p>An LED video wall is built from modular LED panels joined into the dimensions required for an event. When comparing systems, consider:<br/>
                <strong>Wall dimensions:</strong> Use the video wall builder to create a custom format or begin with one of the configured bundles.<br/>
                <strong>Pixel pitch:</strong> A smaller pixel pitch provides more detail for close viewing. The best choice depends on the closest audience distance and the content being displayed.<br/>
                <strong>Mounting:</strong> Depending on the venue and wall size, the display may use a ground-supported frame or an approved truss-mounted installation.<br/>
                
                </p>
                <strong className="bold">How do I play content on the video wall?</strong>
                <p>Most systems accept a laptop or playback device through a video processor. Presentations, videos, cameras, and other sources may require different connections or switching equipment, so our team confirms the source plan before installation. If you have questions, call <a href="tel:+13059744411">305 974 4411</a>.</p>
              
        </OverBanner>
            <PageShell>
                <VisualDecisionGuide
                    id='video-wall-cost-guide'
                    eyebrow='Plan the complete system'
                    heading='What determines LED video wall rental cost?'
                    description='The equipment estimate is driven by the display itself, while the final proposal also reflects the installation, venue, and show-support requirements.'
                    items={costFactors}
                />
            </PageShell>
               <LandingCategories ui='v2' loading='lazy' title className='padTop' options category='visuals' />
            <PageShell>
                <ServiceOverview content={videoWallOverviewContent} />
            </PageShell>
            <section className='stageType'>
          
                <h2 className='upperBold'>Components used in an installed LED video wall</h2>
                <span className='underUpper'>LED panels, processors, and mounting elements are combined into a complete system. Most customers should configure a wall above or request a proposal rather than select individual components.</span>
                <ProductList
                    noTop
                    actionMode='details'
                    eyebrow='Supporting system inventory'
                    listName='LED Panels'
                    products={products.filter(p => p.categories?.includes('Visuals/LED Panels'))}
                />
            </section>    


            {/* <div className="stageTypes">
                <h2 className='upper'>Want to rent LED Wall for your next event?</h2>
                <span className='underUpper'>Choose between outdoor, indoor ot large LED wall rental options.

LED Screens are used in many different events. Concerts and festivals use video walls to project visuals and to stream live the band or DJ performing. Corporate events such as conferences and trade show use video wall display as a visual aid. Churches also use video walls in their services.</span>
            </div> */}
      <RentalFAQV2 questions={faq} headingId='led-video-wall-rental-faq-title' />
            <SimilarCats ui='v2' subCate={subCate} />
            <PastEvents ui='v2' filter={['concert', 'private', 'conference']} newsletterevents={newsletterevents} total_events={total_events} />
            <ClientReviews ui='v2' filter={['trust', 'convenient']} />
        </div>
    </>
}

export default VideoWallRental
